import { useEffect, useCallback } from "@bestowinc/tinkertoy-shell-deps/react";
import { useNavigate, useNavigation, useRevalidator } from "react-router";

import {
  beginRevalidationTracking,
  cancelRevalidationTracking,
  completeRevalidationTracking,
  getActiveRevalidationView,
} from "../performance-telemetry/revalidation-tracking";

import type { RouteState, TinkertoyHook } from "./types";
import { useShellLoaderData } from "./use-shell-loader-data";

/**
 * The sole public API for any component that needs to interact with
 * the routing layer.
 *
 * Composes React Router primitives into a stable interface. If the
 * routing library is ever swapped out, this hook's contract stays
 * the same. Nothing outside this hook should import from react-router.
 *
 * @throws {Error} if called outside the React Router context
 * (i.e. outside the RouterProvider tree set up by mountShell)
 */
export function useTinkertoy(): TinkertoyHook {
  const data = useShellLoaderData();
  const reactRouterNavigate = useNavigate();
  const navigation = useNavigation();
  const { revalidate, state: revalidationState } = useRevalidator();

  const routeState: RouteState = {
    loadedView: data.loadedView,
    viewData: data.viewData,
    moduleDataMap: data.moduleDataMap,
    flowMeta: data.flowMeta,
    isLoading: navigation.state === "loading",
  };

  const navigate = (to: string, options?: { replace?: boolean }) => {
    reactRouterNavigate(to, options);
  };

  const goBack = () => {
    reactRouterNavigate(-1);
  };

  const revalidateWithTelemetry = useCallback(() => {
    beginRevalidationTracking({
      viewId: data.loadedView.viewId,
      viewVersion: data.loadedView.viewVersion,
    });
    revalidate();
  }, [data.loadedView.viewId, data.loadedView.viewVersion, revalidate]);

  useEffect(() => {
    const active = getActiveRevalidationView();
    if (!active) {
      return;
    }
    if (
      active.viewId !== data.loadedView.viewId ||
      active.viewVersion !== data.loadedView.viewVersion
    ) {
      cancelRevalidationTracking();
    }
  }, [data.loadedView.viewId, data.loadedView.viewVersion]);

  useEffect(() => {
    if (revalidationState !== "idle") {
      return;
    }

    const active = getActiveRevalidationView();
    if (!active) {
      return;
    }

    if (
      active.viewId === data.loadedView.viewId &&
      active.viewVersion === data.loadedView.viewVersion
    ) {
      completeRevalidationTracking();
      return;
    }

    cancelRevalidationTracking();
  }, [revalidationState, data.loadedView.viewId, data.loadedView.viewVersion]);

  return {
    routeState,
    navigate,
    goBack,
    revalidate: revalidateWithTelemetry,
    isActionPending: navigation.state === "submitting",
    isRevalidating: revalidationState === "loading",
  };
}
